import { type JSX, useCallback, useRef, useState } from "react";
import { Area, AreaChart, Bar, BarChart } from "recharts";
import { Tooltip, useTooltip } from "widgets";

import { ANNOTATION_TYPE } from "@/settings/settings";

const LINE_COLOR = "#777777";
const BAR_COLOR = "#a7a7a7";
const FILTERED_COLOR = "#ffffff";

export interface IFilterChartValue {
  name: string;
  totalCount: number;
  remainingCount: number;
}

export interface IFilterChartData {
  type: string;
  valueSet: IFilterChartValue[];
}

interface IFilterChartProps {
  width: number;
  height: number;
  chartData: IFilterChartData;
}

interface IFilterChartInput {
  name: string;
  filtered: number;
  available: number;
}

const DEFAULT_TOOLTIP_TEXT = "Move mouse cursor over a bar";

function FilterChart(props: IFilterChartProps): JSX.Element {
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const { triggerProps, tooltipProps, arrowProps, state } = useTooltip(triggerRef, tooltipRef, {
    placement: "right",
    offset: 10,
    delay: 300,
  });

  const [tooltipText, setTooltipText] = useState<string>(DEFAULT_TOOLTIP_TEXT);
  const updateTooltip = useCallback(
    (data) => {
      const p = data?.activePayload?.[0]?.payload;
      const text: string = p ? `${p.name} (${p.available}/${p.available + p.filtered})` : DEFAULT_TOOLTIP_TEXT;
      setTooltipText(text);
    },
    [setTooltipText],
  );

  const { width, height, chartData } = props;

  const filteredChartInput: IFilterChartInput[] = chartData.valueSet.map((v) => {
    return { name: v.name, filtered: v.totalCount - v.remainingCount, available: v.remainingCount };
  });

  return (
    <>
      <div
        className="chart-filters-item-histogram"
        style={{ width, height, display: "inline" }}
        onMouseLeave={() => state.close()}
        {...triggerProps}
        ref={triggerRef}
      >
        {chartData.type === ANNOTATION_TYPE.NUMERICAL ? (
          <AreaChartFilter width={width} height={height} chartData={filteredChartInput} updateTooltip={updateTooltip} />
        ) : (
          <BarChartFilter width={width} height={height} chartData={filteredChartInput} updateTooltip={updateTooltip} />
        )}
      </div>
      {state.isOpen && (
        <Tooltip domProps={tooltipProps} arrowProps={arrowProps} ref={tooltipRef}>
          {tooltipText}
        </Tooltip>
      )}
    </>
  );
}

interface IAreaChartFilterProps {
  chartData: IFilterChartInput[];
  width: number;
  height: number;
  updateTooltip: (data) => void;
}

function AreaChartFilter(props: IAreaChartFilterProps): JSX.Element {
  return (
    <AreaChart width={props.width} height={props.height} data={props.chartData} onMouseMove={props.updateTooltip}>
      <Area
        type="step"
        dataKey="filtered"
        stackId="a"
        stroke={BAR_COLOR}
        fill={FILTERED_COLOR}
        isAnimationActive={false}
      />
      <Area
        type="step"
        dataKey="available"
        stackId="a"
        stroke={BAR_COLOR}
        fill={LINE_COLOR}
        isAnimationActive={false}
      />
    </AreaChart>
  );
}

interface IBarChartFilterProps {
  chartData: IFilterChartInput[];
  width: number;
  height: number;
  updateTooltip: (data) => void;
}

function BarChartFilter(props: IBarChartFilterProps): JSX.Element {
  const CustomBar = (props) => {
    const { stroke, fill, x, y, width, height } = props;

    return <rect x={x} y={y} width={width} height={height} stroke={height > 0 ? stroke : null} fill={fill} />;
  };

  return (
    <BarChart width={props.width} height={props.height} data={props.chartData} onMouseMove={props.updateTooltip}>
      <Bar
        shape={<CustomBar />}
        dataKey="filtered"
        stackId="a"
        fill={FILTERED_COLOR}
        isAnimationActive={false}
        stroke={BAR_COLOR}
      />
      <Bar
        shape={<CustomBar />}
        dataKey="available"
        stackId="a"
        fill={BAR_COLOR}
        isAnimationActive={false}
        stroke={BAR_COLOR}
      />
    </BarChart>
  );
}

export { FilterChart };
